/**
 * PatientElectedRedirectModal.jsx — documentation modal for a Patient-Elected Redirect.
 *
 * What: Collects the required attestations + clinical notes when a patient who
 *       has been assessed elects to leave the ED for a next-day appointment, then
 *       hands the collected fields to the caller to persist.
 * Why:  A patient-elected redirect is an AMA-adjacent, patient-initiated choice
 *       that must be documented as "full evaluation offered and declined" — it is
 *       NOT a clinical clearance.
 * Invariants:
 *   - Copy must never imply clinical clearance. Prohibited phrasing: "cleared to
 *     leave", "safe to go", "low risk", "recommended redirect", "clinician approved".
 *   - Save is disabled until every checkbox is checked AND notes ≥ 10 chars.
 *   - Cancel writes nothing; the card stays in its current stage.
 *
 * Ported from HealthDesk Experimental (Exp-B) — no adaptation.
 * Tightest coupling: SavoirCarePatientCard.jsx (opens this), useSavoirCareBoard
 *   (recordPatientElectedRedirect persists the result).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Box,
} from '@mui/material';

/** Minimum length for the required clinical-notes field. */
const REDIRECT_NOTES_MIN_LENGTH = 10;

/** Non-editable framing shown at the top of the modal. */
const OPENING_LINE =
  'The patient has been offered a complete evaluation and has chosen to leave for a ' +
  'next-day appointment. This is a patient-initiated decision. Complete the documentation below.';

const PatientElectedRedirectModal = ({ open, entry, onCancel, onConfirm }) => {
  // The two offered/declined attestations are always-true per the schema; we still
  // require the clinician to actively check them so the attestation is deliberate.
  const [offeredFullEval, setOfferedFullEval] = useState(false);
  const [patientDeclined, setPatientDeclined] = useState(false);
  const [patientAlertAndOriented, setPatientAlertAndOriented] = useState(false);
  const [returnPrecautions, setReturnPrecautions] = useState(false);
  const [appointmentPathProvided, setAppointmentPathProvided] = useState(false);
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset all fields whenever the dialog is (re)opened for a fresh entry.
  useEffect(() => {
    if (open) {
      setOfferedFullEval(false);
      setPatientDeclined(false);
      setPatientAlertAndOriented(false);
      setReturnPrecautions(false);
      setAppointmentPathProvided(false);
      setClinicalNotes('');
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const notesOk = clinicalNotes.trim().length >= REDIRECT_NOTES_MIN_LENGTH;
  const allChecked =
    offeredFullEval &&
    patientDeclined &&
    patientAlertAndOriented &&
    returnPrecautions &&
    appointmentPathProvided;
  const canSave = allChecked && notesOk && !submitting;

  const handleConfirm = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm({
        offeredFullEval: true,
        patientDeclinedFullEval: true,
        patientAlertAndOriented,
        appointmentPathProvided,
        returnPrecautions,
        clinicalNotes: clinicalNotes.trim(),
      });
    } catch (err) {
      console.error('[SavoirCare] Failed to record patient-elected redirect:', err);
      setSubmitError(err?.message || 'Failed to save. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      data-testid="savoir-care-redirect-modal"
    >
      <DialogTitle>Document patient-elected redirect</DialogTitle>
      <DialogContent>
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          {OPENING_LINE}
        </Alert>

        {entry?.chiefComplaintSummary && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Patient: {entry.chiefComplaintSummary}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={offeredFullEval}
                onChange={(e) => setOfferedFullEval(e.target.checked)}
              />
            }
            label="I offered the patient a complete evaluation"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={patientDeclined}
                onChange={(e) => setPatientDeclined(e.target.checked)}
              />
            }
            label="The patient declined and elected to leave for a next-day appointment"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={patientAlertAndOriented}
                onChange={(e) => setPatientAlertAndOriented(e.target.checked)}
              />
            }
            label="Patient is alert and oriented"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={returnPrecautions}
                onChange={(e) => setReturnPrecautions(e.target.checked)}
              />
            }
            label="Return precautions have been provided to the patient"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={appointmentPathProvided}
                onChange={(e) => setAppointmentPathProvided(e.target.checked)}
              />
            }
            label="A follow-up appointment pathway has been provided"
          />
        </Box>

        <TextField
          label="Clinical notes (required)"
          placeholder="Document the clinical picture that was present at the time of election."
          value={clinicalNotes}
          onChange={(e) => setClinicalNotes(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          sx={{ mt: 2 }}
          error={clinicalNotes.length > 0 && !notesOk}
          inputProps={{ 'data-testid': 'savoir-care-redirect-notes' }}
          helperText={
            clinicalNotes.length > 0 && !notesOk
              ? `Please enter at least ${REDIRECT_NOTES_MIN_LENGTH} characters.`
              : ' '
          }
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canSave}
          data-testid="savoir-care-redirect-save"
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientElectedRedirectModal;
